/**
 * @fileoverview React hook for enforcing metadata-driven mutually exclusive field rules
 * in the dynamic asset form (AssetFormModal).
 *
 * How to use:
 *   const { getFilteredOptions, handleMutualExclusiveChange } =
 *     useMutuallyExclusiveFields(assetPropertyValues, setValue, getValues)
 *
 * Integration:
 *   1. Pass `getFilteredOptions(slug, rawOptions)` as the `options` prop to each field renderer.
 *   2. Call `handleMutualExclusiveChange(slug, newValue)` inside each field's onChange handler.
 *
 * Root cause of v1 failure:
 *   RHF 7.x mutates form values IN PLACE inside `_formValues`. This means
 *   `watch("assetProperty")` returns the SAME array reference across renders even
 *   when values change. A `useMemo([assetPropertyValues])` dependency check never
 *   detects the change and produces a permanently stale map.
 *
 *   Fix:
 *   - `getFilteredOptions`: computes `buildExcludedValuesMap` fresh on every call
 *     (no memoisation) so it always reads the current in-place-mutated values.
 *   - Value purging: moved from `useEffect` (unreliable trigger) to `onChange`
 *     handlers via `handleMutualExclusiveChange`, which uses `getValues` for a
 *     fresh read of the form state at the moment of user interaction.
 */

import { useCallback, useEffect, useRef } from "react"
import {
    buildExcludedValuesMap,
    filterCurrentValue,
    filterExcludedOptions,
    getSelectedValues,
} from "../utils/mutuallyExclusiveFields"

/**
 * Enforces mutually exclusive field constraints declared in field metadata.
 *
 * Metadata shape expected on each field (details.validation):
 * ```json
 * {
 *   "priority": 1,
 *   "linked_fields": [{ "field": "<other-slug>", "rule": "mutually_exclusive" }]
 * }
 * ```
 *
 * Behaviour:
 *  - `getFilteredOptions`: on every render, removes option entries whose values
 *    are currently selected in a higher-priority linked field.
 *  - `handleMutualExclusiveChange`: called from onChange — immediately purges the
 *    newly selected value from any lower-priority linked field's form state.
 *  - An initial `useEffect` runs once after the field array is populated (edit mode)
 *    to resolve conflicts that existed before any user interaction.
 *
 * Priority convention: lower number = higher priority (1 beats 2).
 * No field names are hardcoded — the logic is driven entirely by metadata.
 *
 * Supports:
 *  - `dropdown`              (single-select, stores scalar value)
 *  - `multi-select-dropdown` (multi-select, stores array of {label, value} objects)
 *
 * @param {Array<{slug: string, details: object, assetValue: any}>} assetPropertyValues
 *   The watched `assetProperty` array from `watch("assetProperty")`.
 * @param {import("react-hook-form").UseFormSetValue<any>} setValue
 *   React Hook Form's `setValue` function.
 * @param {import("react-hook-form").UseFormGetValues<any>} getValues
 *   React Hook Form's `getValues` function — always returns the current (fresh) values
 *   regardless of RHF's in-place mutation pattern.
 * @returns {{
 *   getFilteredOptions: (slug: string, options: Array<{label: string, value: any}>) => Array<{label: string, value: any}>,
 *   handleMutualExclusiveChange: (changedSlug: string, newRawValue: any) => void
 * }}
 */
export function useMutuallyExclusiveFields(assetPropertyValues, setValue, getValues) {
    // Track whether we have already run the one-time initial cleanup for edit mode.
    // Resets to false when the field array is cleared (modal closed / asset changed).
    const initialCleanupDoneRef = useRef(false)

    /**
     * One-time initial cleanup: runs when the field array is first populated.
     *
     * Covers edit-mode scenarios where the server returns a form state that already
     * contains conflicts (e.g. both primary and secondary hold the same caregiver).
     * We cannot rely on onChange for these because no user interaction occurred.
     *
     * Uses `buildExcludedValuesMap` called INSIDE the effect (not a memoised value)
     * to guarantee a fresh read of the current in-place-mutated array contents.
     */
    useEffect(() => {
        const len = assetPropertyValues?.length ?? 0

        if (len === 0) {
            // Field array cleared — reset so the next population triggers cleanup again
            initialCleanupDoneRef.current = false
            return
        }

        if (initialCleanupDoneRef.current) return
        initialCleanupDoneRef.current = true

        // Build the exclusion map directly inside the effect (bypasses reference-equality issue)
        const excludedMap = buildExcludedValuesMap(assetPropertyValues)

        assetPropertyValues.forEach((field, index) => {
            const excluded = excludedMap[field.slug]
            if (!excluded || excluded.size === 0) return

            const currentValues = getSelectedValues(field.assetValue, field.details?.type)
            const hasConflict = currentValues.some((v) => excluded.has(v))
            if (!hasConflict) return

            const newValue = filterCurrentValue(
                field.assetValue,
                field.details?.type,
                Array.from(excluded),
            )

            setValue(`assetProperty.${index}.assetValue`, newValue, {
                shouldDirty: true,
                shouldValidate: false,
            })

            if (process.env.NODE_ENV === "development") {
                console.debug(
                    `[useMutuallyExclusiveFields] Initial cleanup — removed conflict from "${field.slug}":`,
                    currentValues.filter((v) => excluded.has(v)),
                )
            }
        })
    }, [assetPropertyValues?.length, setValue]) // intentionally use .length, not the reference

    /**
     * Returns a filtered version of the options array for the given field, excluding
     * any values currently selected in higher-priority linked fields.
     *
     * IMPORTANT: this function intentionally avoids `useMemo` / `useCallback` because
     * RHF 7.x mutates `assetPropertyValues` in place — memoisation based on array
     * reference would always be a cache hit and never reflect actual value changes.
     * Computing `buildExcludedValuesMap` afresh on every call is O(n fields) and
     * negligible for the form sizes used here.
     *
     * @param {string} slug - The field's unique slug identifier.
     * @param {Array<{label: string, value: any}>} options - Raw (unfiltered) options.
     * @returns {Array<{label: string, value: any}>} Options with excluded entries removed.
     */
    /**
     * Applies metadata-driven option filtering rules for a field.
     *
     * Current supported rules:
     * - mutually_exclusive (existing)
     * - filter_options (new dependent dropdown support)
     *
     * Future rules can be added here without touching AssetFormModal.
     */
    const getFilteredOptions = (slug, options) => {
        const currentField = assetPropertyValues?.find((field) => field.slug === slug)

        if (!currentField) {
            return options
        }

        let filteredOptions = options

        /**
         * Rule 1: Mutually Exclusive
         *
         * Existing behaviour.
         * Removes values already selected in higher-priority linked fields.
         */
        const excludedMap = buildExcludedValuesMap(assetPropertyValues ?? [])
        const excluded = excludedMap[slug]

        filteredOptions = filterExcludedOptions(filteredOptions, excluded)

        /**
         * Rule 2: Filter Options
         *
         * Metadata Example:
         *
         * {
         *   "rule": "filter_options",
         *   "field": "patient",
         *   "mapping_key": "patient_id"
         * }
         *
         * Filters available options based on the selected value
         * of another field.
         */
        const linkedFields = currentField?.details?.validation?.linked_fields ?? []

        linkedFields.forEach((rule) => {
            if (rule.rule !== "filter_options") {
                return
            }

            const parentField = assetPropertyValues?.find((field) => field.slug === rule.field)

            const parentValue = parentField?.assetValue

            /**
             * Parent not selected yet.
             *
             * Show no options until a value is selected.
             */
            if (!parentValue) {
                filteredOptions = []
                return
            }

            filteredOptions = filteredOptions.filter((option) => {
                return option?.[rule.mapping_key] === parentValue
            })
        })

        return filteredOptions
    }

    /**
     * Call this inside a field's onChange handler immediately after updating the
     * field's own value. It synchronously purges the newly selected value(s) from
     * any lower-priority mutually exclusive linked fields using `getValues` for a
     * guaranteed-fresh read of current form state.
     *
     * Using onChange (rather than useEffect) sidesteps the RHF in-place mutation
     * reference-stability problem entirely — `getValues` always reads the live
     * `_formValues` object regardless of reference identity.
     *
     * @param {string} changedSlug - The slug of the field that just changed.
     * @param {any} newRawValue - The new raw value passed to field.onChange()
     *   (scalar string for dropdown; array of {label,value} for multi-select-dropdown).
     */
    const handleMutualExclusiveChange = useCallback(
        (changedSlug, newRawValue) => {
            const allFields = getValues("assetProperty") || []

            const changedFieldIndex = allFields.findIndex((field) => field.slug === changedSlug)

            if (changedFieldIndex === -1) return

            const changedField = allFields[changedFieldIndex]

            /**
             * --------------------------------------------------
             * RULE 1 : MUTUALLY EXCLUSIVE (EXISTING BEHAVIOUR)
             * --------------------------------------------------
             */

            const priority = changedField.details?.validation?.priority

            const mutuallyExclusiveFields = (
                changedField.details?.validation?.linked_fields ?? []
            ).filter((rule) => rule.rule === "mutually_exclusive")

            if (mutuallyExclusiveFields.length && priority != null) {
                const selectedValues = getSelectedValues(newRawValue, changedField.details?.type)

                mutuallyExclusiveFields.forEach(({ field: linkedSlug }) => {
                    const linkedIndex = allFields.findIndex((field) => field.slug === linkedSlug)

                    if (linkedIndex === -1) return

                    const linkedField = allFields[linkedIndex]

                    const linkedPriority = linkedField.details?.validation?.priority

                    if (linkedPriority == null || priority >= linkedPriority) {
                        return
                    }

                    if (!selectedValues.length) {
                        return
                    }

                    const currentValues = getSelectedValues(
                        linkedField.assetValue,
                        linkedField.details?.type,
                    )

                    const hasConflict = currentValues.some((value) =>
                        selectedValues.includes(value),
                    )

                    if (!hasConflict) {
                        return
                    }

                    const newLinkedValue = filterCurrentValue(
                        linkedField.assetValue,
                        linkedField.details?.type,
                        selectedValues,
                    )

                    setValue(`assetProperty.${linkedIndex}.assetValue`, newLinkedValue, {
                        shouldDirty: true,
                        shouldValidate: false,
                    })
                })
            }

            /**
             * --------------------------------------------------
             * RULE 2 : FILTER OPTIONS
             * --------------------------------------------------
             *
             * Reset dependent field values when parent changes.
             *
             * Example:
             *
             * Patient -> Care Plan
             *
             * Patient changes
             * => Clear Care Plan
             */
            allFields.forEach((field, index) => {
                const linkedFields = field?.details?.validation?.linked_fields ?? []

                const dependencyRule = linkedFields.find(
                    (rule) =>
                        rule.rule === "filter_options" &&
                        rule.field === changedSlug &&
                        rule.reset_on_parent_change,
                )

                if (!dependencyRule) {
                    return
                }

                const currentValue = field.assetValue

                if (currentValue === null || currentValue === undefined || currentValue === "") {
                    return
                }

                setValue(`assetProperty.${index}.assetValue`, null, {
                    shouldDirty: true,
                    shouldValidate: true,
                })
            })
        },
        [getValues, setValue],
    )

    return { getFilteredOptions, handleMutualExclusiveChange }
}
